import React from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react-lite';
import { FormattedMessage, useIntl } from 'react-intl';

import {
    LocationSelector,
    LegacyCityPropType,
    useForm,
    FilteredAsyncContainer,
    BOOLEAN_OPTIONS,
    FormValue,
    Select,
    CurrencyInput,
} from '@ratehub/base-ui';
import {
    SCENARIO_TYPES,
    PAYMENT_FREQUENCY_MESSAGES,
    QuestionAmortization,
    QuestionRateType,
    QuestionRateTerm,
    QuestionOccupancy,
    DownPaymentInput,
} from '@ratehub/mtg-common';
import { PAYMENT_FREQUENCIES, INTENDED_USE } from '@ratehub/documents/mtg-journey-document/stable';

import { Messages as MTG_SHARED_MESSAGES } from '../definitions/MortgageTerms';


function ProductTableMortgageFilteredAsyncContainer({
    names,
    values,
    excludedFieldNames = [],
    children,
    ...otherProps
}) {
    const intl = useIntl();
    const formState = useForm();

    // Ensure our city inputs are in sync
    function handleLocationChange(value) {
        // fieldNames.helocCity is rendered first then immediately calls it's onChange event, before fieldNames.city has rendered.
        // both fields are required (by default) and need to be in sync with each other. This gate ensures both fields are rendered before setting either of their values.
        if (!!formState.getFieldNamed(fieldNames.city) && !!formState.getFieldNamed(fieldNames.helocCity)) {
            if (scenario === SCENARIO_TYPES.HELOC) {
                formState.getFieldNamed(fieldNames.city).setValue(value);
            } else {
                formState.getFieldNamed(fieldNames.helocCity).setValue(value);
            }
        }
    }

    const fieldNames = {
        ...DEFAULT_NAMES,
        ...names,
    };

    const scenario = formState?.values?.[fieldNames.scenario] ?? values?.scenario; // Fallback to values.scenario if Form values aren't set yet

    return (
        <FilteredAsyncContainer
            primaryFilters={[
                (
                    <Select
                        key={fieldNames.scenario}
                        name={fieldNames.scenario}
                        value={values?.scenario}
                        options={getScenarioOptions()}
                        label={(
                            <FormattedMessage
                                id="web-components.mortgageRateTableV2+Filter.scenarioLabel"
                                defaultMessage="Transaction type"
                            />
                        )}
                        className="mortgage-filter-question-compact"
                        errorMessageStyle="minimal"
                        /* Removing the animations for all Questions here since the delay looked off with the layout */
                        animationType="none"
                    />
                ),
                (
                    <If
                        key={fieldNames.type}
                        condition={!excludedFieldNames.includes('type')}
                    >
                        <QuestionRateType
                            key={fieldNames.type}
                            name={fieldNames.type}
                            value={values?.type}
                            areOptionsExtended={true}

                            className="mortgage-filter-question-compact"
                            errorMessageStyle="minimal"
                            animationType="none"
                            isVisible={scenario !== SCENARIO_TYPES.HELOC}
                        />
                    </If>
                ),
                (
                    <CurrencyInput
                        key={fieldNames.homePrice}
                        name={fieldNames.homePrice}
                        value={values?.homePrice}
                        label={(
                            <FormattedMessage
                                id="web-components.mortgageRateTableV2+Filter.homePriceLabel"
                                defaultMessage="Purchase price"
                            />
                        )}
                        decimalScale={0}
                        className="mortgage-filter-question-compact"
                        errorMessageStyle="minimal"
                        animationType="none"
                        isVisible={scenario === SCENARIO_TYPES.PURCHASE}
                    />
                ),
                (
                    <FormValue
                        key={fieldNames.downPayment}
                    >
                        {(_values, form) => {
                            // default prevents jank on first render before homePrice is registered in the form
                            const purchasePrice = form.getValue(fieldNames.homePrice) ?? values?.homePrice;

                            return (
                                <DownPaymentInput
                                    name={fieldNames.downPayment}
                                    value={values?.downPayment}
                                    purchasePrice={purchasePrice}
                                    isInline
                                    isCompact
                                    isVisible={scenario === SCENARIO_TYPES.PURCHASE}
                                    animationType="none"
                                    percentProps={{ isPercentSymbolVisible: false, decimalScale: 1 }}
                                    errorMessageStyle="minimal"
                                    className="mortgage-filter-question-compact"
                                    // show immediate validation feedback in case incoming filters are invalid
                                    canShowInvalid={!!purchasePrice}
                                />
                            );
                        }}
                    </FormValue>
                ),
                (
                    <CurrencyInput
                        key={fieldNames.helocAmount}
                        name={fieldNames.helocAmount}
                        value={values?.helocAmount}
                        label={(
                            <FormattedMessage
                                id="web-components.mortgageRateTableV2+Filter.helocAmountLabel"
                                defaultMessage="HELOC Amount"
                            />
                        )}
                        decimalScale={0}
                        className="mortgage-filter-question-compact"
                        errorMessageStyle="minimal"
                        animationType="none"
                        isVisible={scenario === SCENARIO_TYPES.HELOC}
                    />
                ),
                (
                    <CurrencyInput
                        key={fieldNames.renewMortgageBalance}
                        name={fieldNames.renewMortgageBalance}
                        value={values?.renewMortgageBalance}
                        label={(
                            <FormattedMessage
                                id="web-components.mortgageRateTableV2+Filter.renewCurrentMortgageBalanceLabel"
                                defaultMessage="Current mortgage balance"
                            />
                        )}
                        decimalScale={0}
                        className="mortgage-filter-question-compact"
                        errorMessageStyle="minimal"
                        animationType="none"
                        isVisible={scenario === SCENARIO_TYPES.RENEWAL}
                    />
                ),
                (
                    <QuestionAmortization
                        key={fieldNames.renewAmortization}
                        name={fieldNames.renewAmortization}
                        value={values?.renewAmortization}

                        className="mortgage-filter-question-compact"
                        errorMessageStyle="minimal"
                        animationType="none"
                        isVisible={scenario === SCENARIO_TYPES.RENEWAL}
                    />
                ),
                (
                    <CurrencyInput
                        key={fieldNames.refinanceMortgageBalance}
                        name={fieldNames.refinanceMortgageBalance}
                        value={values?.refinanceMortgageBalance}
                        label={(
                            <FormattedMessage
                                id="web-components.mortgageRateTableV2+Filter.refianceCurrentMortgageBalanceLabel"
                                defaultMessage="Current mortgage balance"
                            />
                        )}
                        decimalScale={0}
                        className="mortgage-filter-question-compact"
                        errorMessageStyle="minimal"
                        animationType="none"
                        isVisible={scenario === SCENARIO_TYPES.REFINANCE}
                    />
                ),
                (
                    <CurrencyInput
                        key={fieldNames.additionalFunds}
                        name={fieldNames.additionalFunds}
                        value={values?.additionalFunds}
                        label={(
                            <FormattedMessage
                                id="web-components.mortgageRateTableV2+Filter.additionalFundsLabel"
                                defaultMessage="Additional funds needed"
                            />
                        )}
                        decimalScale={0}
                        className="mortgage-filter-question-compact"
                        errorMessageStyle="minimal"
                        animationType="none"
                        isVisible={scenario === SCENARIO_TYPES.REFINANCE}
                    />
                ),
                (
                    <LocationSelector
                        name={fieldNames.helocCity}
                        key={fieldNames.helocCity}
                        initialProvince={values?.province}
                        value={values?.city}
                        label={(
                            <FormattedMessage
                                id="web-components.mortgageRateTableV2+Filter.helocCityLabel"
                                defaultMessage="Location"
                            />
                        )}
                        isRequired
                        className="mortgage-filter-question-compact"
                        errorMessageStyle="minimal"
                        animationType="none"
                        onChange={handleLocationChange}
                        isVisible={scenario === SCENARIO_TYPES.HELOC}
                    />
                ),
            ]}

            additionalFilters={[
                (
                    <If
                        key={fieldNames.term}
                        condition={!excludedFieldNames.includes('term')}
                    >
                        <QuestionRateTerm
                            key={fieldNames.term}
                            name={fieldNames.term}
                            value={values?.term}
                            className="mortgage-filter-question-compact"
                            errorMessageStyle="minimal"
                            animationType="none"
                            isVisible={scenario !== SCENARIO_TYPES.HELOC}
                        />
                    </If>
                ),
                (
                    <If
                        key={fieldNames.city}
                        condition={!excludedFieldNames.includes('city')}
                    >
                        <LocationSelector
                            key={fieldNames.city}
                            name={fieldNames.city}
                            initialProvince={values?.province}
                            value={values?.city}
                            label={(
                                <FormattedMessage
                                    id="web-components.mortgageRateTableV2+Filter.locationLabel"
                                    defaultMessage="Location"
                                />
                            )}
                            isRequired
                            className="mortgage-filter-question-compact"
                            errorMessageStyle="minimal"
                            animationType="none"
                            isVisible={scenario !== SCENARIO_TYPES.HELOC}
                            onChange={handleLocationChange}
                        />
                    </If>
                ),
                (
                    <If
                        key={fieldNames.purchaseRefinanceAmortization}
                        condition={!excludedFieldNames.includes('purchaseRefinanceAmortization')}
                    >
                        <QuestionAmortization
                            key={fieldNames.purchaseRefinanceAmortization}
                            name={fieldNames.purchaseRefinanceAmortization}
                            value={values?.purchaseRefinanceAmortization}

                            className="mortgage-filter-question-compact"
                            errorMessageStyle="minimal"
                            animationType="none"
                            isVisible={scenario === SCENARIO_TYPES.PURCHASE || scenario === SCENARIO_TYPES.REFINANCE}
                        />
                    </If>
                ),
                (
                    <If
                        key={fieldNames.occupancy}
                        condition={!excludedFieldNames.includes('occupancy')}
                    >
                        <QuestionOccupancy
                            key={fieldNames.occupancy}
                            name={fieldNames.occupancy}
                            value={values?.occupancy}

                            className="mortgage-filter-question-compact"
                            errorMessageStyle="minimal"
                            animationType="none"
                            isVisible={scenario !== SCENARIO_TYPES.HELOC}
                        />
                    </If>
                ),
                (
                    <If
                        key={fieldNames.paymentFrequency}
                        condition={!excludedFieldNames.includes('paymentFrequency')}
                    >
                        <Select
                            key={fieldNames.paymentFrequency}
                            name={fieldNames.paymentFrequency}
                            value={values?.paymentFrequency}
                            options={PAYMENT_FREQUENCY_OPTIONS}
                            renderOptionLabel={value => intl.formatMessage(PAYMENT_FREQUENCY_MESSAGES[value])}
                            label={(
                                <FormattedMessage
                                    id="web-components.mortgageRateTableV2+Filter.paymentFrequencyLabel"
                                    defaultMessage="Payment frequency"
                                />
                            )}
                            className="mortgage-filter-question-compact"
                            errorMessageStyle="minimal"
                            animationType="none"
                            isVisible={scenario !== SCENARIO_TYPES.HELOC}
                        />
                    </If>
                ),
                (
                    <If
                        key={fieldNames.isCMHC}
                        condition={!excludedFieldNames.includes('isCMHC')}
                    >
                        <Select
                            key={fieldNames.isCMHC}
                            name={fieldNames.isCMHC}
                            value={~~values?.isCMHC}
                            // This is temp workaround and will be addressed in the following bug ticket
                            // https://ratehub.atlassian.net/browse/FIT-3958
                            options={CMHC_OPTIONS}
                            label={(
                                <FormattedMessage
                                    id="web-components.mortgageRateTableV2+Filter.isCHMCLabel"
                                    defaultMessage="CMHC insurance on this mortgage?"
                                />
                            )}
                            className="mortgage-filter-question-compact"
                            errorMessageStyle="minimal"
                            animationType="none"
                            isVisible={scenario === SCENARIO_TYPES.RENEWAL}
                        />
                    </If>
                ),
            ]}

            columnCount={3}
            areAdditionalFiltersHidden={scenario === SCENARIO_TYPES.HELOC}

            {...otherProps}
        >
            {children}
        </FilteredAsyncContainer>
    );
}

ProductTableMortgageFilteredAsyncContainer.propTypes = {
    names: PropTypes.shape({
        scenario: PropTypes.string,
        term: PropTypes.string,
        type: PropTypes.string,
        city: PropTypes.string,
        helocCity: PropTypes.string,
        homePrice: PropTypes.string,
        renewAmortization: PropTypes.string,
        purchaseRefinanceAmortization: PropTypes.string,
        downPayment: PropTypes.string,
        paymentFrequency: PropTypes.string,
        occupancy: PropTypes.string,
        renewMortgageBalance: PropTypes.string,
        isCMHC: PropTypes.string,
        refinanceMortgageBalance: PropTypes.string,
        additionalFunds: PropTypes.string,
        helocAmount: PropTypes.string,
    }),

    values: PropTypes.shape({
        scenario: PropTypes.string.isRequired,
        term: PropTypes.oneOfType([ PropTypes.string, PropTypes.number ]),
        type: PropTypes.string,
        province: PropTypes.string,
        city: LegacyCityPropType,
        homePrice: PropTypes.number,
        renewAmortization: PropTypes.number,
        purchaseRefinanceAmortization: PropTypes.number,
        downPayment: PropTypes.shape({
            dollars: PropTypes.number,
            percent: PropTypes.number,
        }),
        paymentFrequency: PropTypes.oneOf(Object.values(PAYMENT_FREQUENCIES)),
        occupancy: PropTypes.oneOf(Object.values(INTENDED_USE)),
        renewMortgageBalance: PropTypes.number,
        isCMHC: PropTypes.bool,
        refinanceMortgageBalance: PropTypes.number,
        additionalFunds: PropTypes.number,
        helocAmount: PropTypes.number,
    }),

    // We only want the questions within additional filters to be able to be excluded
    excludedFieldNames: PropTypes.arrayOf(
        PropTypes.oneOf([
            'type',
            'term',
            'city',
            'purchaseRefinanceAmortization',
            'occupancy',
            'paymentFrequency',
            'isCMHC',
        ]),
    ),

    children: PropTypes.func.isRequired,
};


const DEFAULT_NAMES = {
    scenario: 'scenario',
    term: 'term',
    type: 'type',
    city: 'city',
    helocCity: 'helocCity',
    homePrice: 'homePrice',
    renewAmortization: 'renewAmortization',
    purchaseRefinanceAmortization: 'purchaseRefinanceAmortization',
    downPayment: 'downPayment',
    paymentFrequency: 'paymentFrequency',
    occupancy: 'occupancy',
    renewMortgageBalance: 'renewMortgageBalance',
    isCMHC: 'isCMHC',
    refinanceMortgageBalance: 'refinanceMortgageBalance',
    additionalFunds: 'additionalFunds',
    helocAmount: 'helocAmount',
};

const PAYMENT_FREQUENCY_OPTIONS = Object.values(PAYMENT_FREQUENCIES);

const CMHC_OPTIONS = BOOLEAN_OPTIONS.map(({ value, label }) => ({
    value: ~~value,
    label,
}));

function getScenarioOptions() {
    return (
        Object.values(SCENARIO_TYPES)
            // Business requirement - Product tables don't offer pre-approval
            .filter(value => value !== SCENARIO_TYPES.PRE_APPROVAL)
            .map(value => ({
                label: MTG_SHARED_MESSAGES[value],
                value: value,
            }))
    );
}

const StyledProductTableMortgageFilteredAsyncContainer = styled(observer(ProductTableMortgageFilteredAsyncContainer))`
    .mortgage-filter-question-compact {
        margin: 0;
    }
`;

export default StyledProductTableMortgageFilteredAsyncContainer;
